// ---------------------------------------------------------------------------
// /creatives — dash page for the creative-hosting list (feature #84).
//
// Route: mounts at `/creatives` via the file-based TanStack Router
// convention. Lets an operator pick a campaign + type filter, see all
// uploaded creative metadata in a table, and hit a stub Upload CTA.
//
// Owned by OPUS-D16. Consumes:
//   - `../features/creatives/api` (hook + helpers)
//   - `../features/creatives/CreativesTable` (table component)
// ---------------------------------------------------------------------------
import { useMemo, useState, type ChangeEvent, type ReactElement } from 'react';
import {
  CREATIVE_TYPES,
  filterByType,
  useCreatives,
  type TypeFilter,
} from '../features/creatives/api';
import { CreativesTable } from '../features/creatives/CreativesTable';

function CreativesPage() {
  const [campaignId, setCampaignId] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const query = useCreatives(campaignId);

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    return filterByType(items, typeFilter);
  }, [query.data, typeFilter]);

  const onCampaignChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCampaignId(e.target.value.trim());
  };
  const onTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setTypeFilter(e.target.value as TypeFilter);
  };
  const onUploadClick = () => {
    // Stub: real upload lands in a later feature (R2 PUT + POST /api/v1/creative-hosting).
    // Keep this handler so the CTA is not a dead button in local smoke.
    if (typeof window !== 'undefined') {
      window.alert(
        'Upload flow coming soon — create via POST /api/v1/creative-hosting for now.',
      );
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 font-[Inter,ui-sans-serif,system-ui]">
      <div className="mx-auto max-w-7xl px-6 py-10">
        <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-3xl font-semibold tracking-tight text-transparent">
              Creatives
            </h1>
            <p className="mt-1 text-sm text-zinc-600">
              Hosted creative assets attached to campaigns. Pick a campaign to
              load its uploaded banners, videos, HTML zips, landing pages and
              other assets.
            </p>
          </div>

          <button
            type="button"
            onClick={onUploadClick}
            className="inline-flex items-center rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm transition hover:border-sky-300 hover:text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2"
          >
            Upload creative
          </button>
        </header>

        <section
          aria-label="filters"
          className="mb-6 flex flex-wrap items-end gap-4 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm"
        >
          <label className="flex min-w-[240px] flex-1 flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Campaign ID
            </span>
            <input
              type="text"
              value={campaignId}
              onChange={onCampaignChange}
              placeholder="e.g. cmp_01HX…"
              className="rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-sm text-zinc-800 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </label>

          <label className="flex min-w-[180px] flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Type
            </span>
            <select
              value={typeFilter}
              onChange={onTypeChange}
              className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">All types</option>
              {CREATIVE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <div className="ml-auto text-xs text-zinc-500">
            {query.isFetching ? (
              <span>Refreshing…</span>
            ) : (
              <span>
                {rows.length} shown
                {query.data ? ` of ${query.data.count} total` : ''}
              </span>
            )}
          </div>
        </section>

        {query.isError ? (
          <div className="mb-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Failed to load creatives.{' '}
            {query.error instanceof Error
              ? query.error.message
              : 'Unknown error.'}
          </div>
        ) : null}

        {campaignId.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 bg-white px-6 py-12 text-center text-sm text-zinc-500">
            Enter a campaign ID above to load its creatives.
          </div>
        ) : (
          <CreativesTable data={rows} isLoading={query.isLoading} />
        )}
      </div>
    </div>
  );
}

// Deferred file-route binding: the scaffold's router tree (once generated
// by `@tanstack/router-plugin`) provides the path-typed `createFileRoute`.
// Until the scaffold lands, we dynamically bind the route so this file is
// still a valid self-contained ESM module and `bun run build` (Vite's
// router plugin) can pick it up via the `Route` export.
import { createFileRoute } from '@tanstack/react-router';

// `createFileRoute` is typed against a generated route tree; in the
// bootstrap state the path union is `undefined`. Cast locally so this
// file compiles before the scaffold emits `routeTree.gen.ts`.
const makeRoute = createFileRoute as unknown as (
  path: string,
) => (opts: { component: () => ReactElement }) => unknown;

export const Route = makeRoute('/creatives/')({
  component: CreativesPage,
});

export default CreativesPage;
